import { NgIf } from '@angular/common';
import { HttpContext } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TenantUserRoles } from '@buspro/shared/common';
import { GroupedOperationsDto, OperationsApi, TenantUsersApi } from '@buspro/web/apis';
import { CacheKeysEnum, TenantAuthService, TenantRolesService } from '@buspro/web/core';
import { ThemeService } from '@buspro/web/core';
import { NgxAuthService } from '@corelabs/ng-auth';
import { NGX_LOADING_BAR_IGNORED } from '@ngx-loading-bar/http-client';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { AvatarModule } from 'ngx-avatars';
import { NgxPermissionsModule } from 'ngx-permissions';
import { lastValueFrom } from 'rxjs';
import { BrandComponent } from '../brand/brand.component';
import { SidebarNavGroup, SidebarNavGroupComponent } from '../sidebar';

const BUS_STOP_PARCEL_PAGES = ['Ready for Pickup', 'Delivered'];
const COLLECTIONS_PAGES = ['Collections', 'Delivery Manifests'];
const PICKUP_PAGES = ['Received', 'Ready for Pickup', 'Delivered', 'Missing'];

/**
 * The people who run operations. Directors, Management Members and Accountants also see this group,
 * but only the pages they read: Ended Trips and Future Bookings.
 */
const OPERATIONS_STAFF = [
  TenantUserRoles.TENANT_ADMIN,
  TenantUserRoles.TENANT_TERMINAL_MANAGER,
  TenantUserRoles.TENANT_BOOKING_MANAGER,
  TenantUserRoles.TERMINAL_BOOKING_CLERK,
];

@Component({
  selector: 'tenant-menu-left-nav',
  templateUrl: './menu-left-nav.component.html',
  styleUrls: ['./menu-left-nav.component.scss'],
  imports: [
    NgIf,
    NgxPermissionsModule,
    BrandComponent,
    RouterLink,
    RouterLinkActive,
    SidebarNavGroupComponent,
    AvatarModule,
  ],
  standalone: true,
})
export class MenuLeftNavComponent implements OnInit {
  private roleService = inject(TenantRolesService);
  private ngxAuthService = inject(NgxAuthService);
  private authService = inject(TenantAuthService);
  private operationsApi = inject(OperationsApi);
  private tenantUsersApi = inject(TenantUsersApi);
  protected themeService = inject(ThemeService);

  @Output() toggleMenu = new EventEmitter<boolean>();
  @Output() collapsedChange = new EventEmitter<boolean>();
  @Output() logoutRequest = new EventEmitter<void>();
  @Input() isOpen = true;
  @Input() mobileDrawer = false;

  roles = TenantUserRoles;
  isCollapsed = signal<boolean>(this.loadCollapseState());

  // Use toSignal at the class level, not inside computed
  authUser = toSignal(this.ngxAuthService.state, { initialValue: null });

  private context = new HttpContext().set(NGX_LOADING_BAR_IGNORED, true);
  private refetchInterval = 7000;

  loadingOperationsQuery = injectQuery(() => ({
    queryKey: [CacheKeysEnum.LOADING_OPERATIONS_GROUPED_BY_DATE, 'sidebar-loading'],
    queryFn: () =>
      lastValueFrom(this.operationsApi.operationsControllerGetLoadingOperationGroupedByDate(undefined, this.context)),
    select: (data: GroupedOperationsDto[]) => data,
    refetchInterval: this.refetchInterval,
    refetchIntervalInBackground: true,
  }));

  inTransitOperationsQuery = injectQuery(() => ({
    queryKey: [CacheKeysEnum.IN_TRANSIT_OPERATIONS_GROUPED_BY_DATE, 'sidebar-in-transit'],
    queryFn: () =>
      lastValueFrom(this.operationsApi.operationsControllerGetInTransitOperationGroupedByDate(undefined, this.context)),
    select: (data: GroupedOperationsDto[]) => data,
    refetchInterval: this.refetchInterval,
    refetchIntervalInBackground: true,
  }));

  loadingCount = computed(() => {
    const data = this.loadingOperationsQuery.data();
    if (!data) return 0;
    return data.reduce((acc, group) => acc + (group.operations?.length || 0), 0);
  });

  inTransitCount = computed(() => {
    const data = this.inTransitOperationsQuery.data();
    if (!data) return 0;
    return data.reduce((acc, group) => acc + (group.operations?.length || 0), 0);
  });

  totalActiveCount = computed(() => {
    return this.loadingCount() + this.inTransitCount();
  });

  // Menu Groups Configuration
  insightsGroup: SidebarNavGroup = {
    label: 'Insights',
    icon: 'fe-bar-chart',
    items: [
      {
        label: 'Dashboard',
        route: '/dashboard',
        icon: 'fe-bar-chart',
      },
      {
        label: 'Monitor',
        route: '/monitor',
        icon: 'fa-dashboard',
        iconType: 'fa',
        roles: [
          TenantUserRoles.TENANT_ADMIN,
          TenantUserRoles.TENANT_TERMINAL_MANAGER,
          TenantUserRoles.TENANT_BOOKING_MANAGER,
          TenantUserRoles.TENANT_PARCEL_MANAGER,
          TenantUserRoles.BUS_STOP_MANAGER,
          TenantUserRoles.BUS_STOP_CLERK,
          TenantUserRoles.TENANT_CONDUCTOR,
        ],
      },
      {
        // One way in: the Reports page lists what this person may open, with search and pins.
        // Mirrors the server's REPORT_READERS; clerks read no company money.
        label: 'Reports',
        route: '/reports',
        icon: 'fe-file-text',
        roles: [
          TenantUserRoles.TENANT_ADMIN,
          TenantUserRoles.TENANT_DIRECTOR,
          TenantUserRoles.TENANT_ACCOUNTANT,
          TenantUserRoles.TENANT_MANAGEMENT_MEMBER,
          TenantUserRoles.TENANT_TERMINAL_MANAGER,
          TenantUserRoles.TENANT_BOOKING_MANAGER,
          TenantUserRoles.TENANT_PARCEL_MANAGER,
        ],
      },
    ],
    defaultExpanded: true,
  };

  operationsGroup: SidebarNavGroup = {
    label: 'Operations',
    icon: 'fe-activity',
    defaultExpanded: true,
    items: [
      // Available Buses and Terminal Available Buses are now accessible from the header widget
      // {
      //   label: 'Available Buses',
      //   route: '/operations/available-buses',
      //   icon: 'fa-bus',
      //   iconType: 'fa',
      //   exact: true,
      // },
      // {
      //   label: 'Terminal Available Buses',
      //   route: '/operations/terminal-available-buses',
      //   icon: 'fa-bus',
      //   iconType: 'fa',
      //   exact: true,
      // },
      {
        label: 'Terminal Live Bookings',
        roles: OPERATIONS_STAFF,
        route: '/operations/terminal-live-bookings',
        icon: 'fa-building-o',
        iconType: 'fa',
        exact: true,
      },
      {
        label: 'Bus Stop Live Bookings',
        roles: OPERATIONS_STAFF,
        route: '/operations/bus-stop-live-bookings',
        icon: 'fe-map-pin',
        exact: true,
      },
      {
        label: 'Schedules',
        route: '/operations/schedules',
        icon: 'fa-sliders',
        iconType: 'fa',
        exact: true,
        roles: [TenantUserRoles.TENANT_ADMIN], // Only visible to Tenant Admin
      },
      {
        label: 'Terminal Schedules',
        route: '/operations/schedules/terminal',
        icon: 'fa-sliders',
        iconType: 'fa',
        exact: true,
        roles: [
          TenantUserRoles.TENANT_TERMINAL_MANAGER,
          TenantUserRoles.TENANT_PARCEL_MANAGER,
          TenantUserRoles.TENANT_BOOKING_MANAGER,
        ], // Visible to Terminal Managers, Parcel Managers, and Booking Managers
      },
      {
        label: 'Active Operations',
        roles: OPERATIONS_STAFF,
        route: '/operations/active',
        icon: 'fe-activity',
        exact: true,
        badge: 0,
      },
      {
        label: 'Ended Trips',
        route: '/operations/completed',
        icon: 'fe-bookmark',
        exact: true,
      },
      {
        label: 'Future Bookings',
        route: '/booking/futures',
        icon: 'fe-calendar',
        exact: true,
        // Mirrors the deny on the server's future-bookings controller. The last three read only.
        roles: [
          TenantUserRoles.TENANT_ADMIN,
          TenantUserRoles.TENANT_TERMINAL_MANAGER,
          TenantUserRoles.TENANT_BOOKING_MANAGER,
          TenantUserRoles.TERMINAL_BOOKING_CLERK,
          TenantUserRoles.TENANT_DIRECTOR,
          TenantUserRoles.TENANT_ACCOUNTANT,
          TenantUserRoles.TENANT_MANAGEMENT_MEMBER,
        ],
      },
      {
        label: 'Ticket Search',
        roles: OPERATIONS_STAFF,
        route: '/booking/tickets-search',
        icon: 'fe-check-square',
        exact: true,
      },
      {
        label: 'Refunds',
        roles: OPERATIONS_STAFF,
        route: '/refunds',
        icon: 'fe-dollar-sign',
        exact: true,
      },
    ],
  };

  /**
   * Reports stand alone rather than under Operations: Accountants and Directors read them but have
   * no Operations menu. The server's catalogue decides which reports each reader sees.
   */
  /**
   * Cash handovers (docs/CASH_ACCOUNTING_PLAN.md, Phase E). Terminal cash for
   * the people who count cash in; the company-wide pages for the office.
   */
  cashGroup: SidebarNavGroup = {
    label: 'Cash',
    icon: 'fe-dollar-sign',
    defaultExpanded: false,
    items: [
      {
        // What the signed-in person holds — anyone who takes money at a desk.
        label: 'My cash',
        route: '/cash/mine',
        icon: 'fe-credit-card',
        exact: true,
        roles: [
          TenantUserRoles.TENANT_ADMIN,
          TenantUserRoles.TENANT_TERMINAL_MANAGER,
          TenantUserRoles.TENANT_BOOKING_MANAGER,
          TenantUserRoles.TENANT_PARCEL_MANAGER,
          TenantUserRoles.TERMINAL_BOOKING_CLERK,
          TenantUserRoles.TERMINAL_PARCEL_CLERK,
        ],
      },
      {
        label: 'Terminal cash',
        route: '/cash/terminals',
        icon: 'fe-inbox',
        exact: true,
        roles: [
          TenantUserRoles.TENANT_ADMIN,
          TenantUserRoles.TENANT_ACCOUNTANT,
          TenantUserRoles.TENANT_TERMINAL_MANAGER,
          TenantUserRoles.TENANT_BOOKING_MANAGER,
          // Counts in the parcel desk's cash (desk section only — see
          // CASH_DESK_RECEIVERS in the cash module).
          TenantUserRoles.TENANT_PARCEL_MANAGER,
        ],
      },
      {
        label: 'Who holds money',
        route: '/cash/balances',
        icon: 'fe-users',
        exact: true,
        roles: [TenantUserRoles.TENANT_ADMIN, TenantUserRoles.TENANT_ACCOUNTANT],
      },
      {
        label: 'Shortfalls',
        route: '/cash/shortfalls',
        icon: 'fe-alert-triangle',
        exact: true,
        roles: [TenantUserRoles.TENANT_ADMIN, TenantUserRoles.TENANT_ACCOUNTANT],
      },
      {
        label: 'Deductions',
        route: '/cash/deductions',
        icon: 'fe-file-minus',
        exact: true,
        roles: [TenantUserRoles.TENANT_ADMIN, TenantUserRoles.TENANT_ACCOUNTANT],
      },
      {
        label: 'Take in to office',
        route: '/cash/take-in',
        icon: 'fe-briefcase',
        exact: true,
        roles: [TenantUserRoles.TENANT_ADMIN, TenantUserRoles.TENANT_ACCOUNTANT],
      },
      {
        label: 'Reconciliation',
        route: '/cash/reconciliation',
        icon: 'fe-check-square',
        exact: true,
        roles: [TenantUserRoles.TENANT_ADMIN, TenantUserRoles.TENANT_ACCOUNTANT],
      },
    ],
  };

  /** Updates the Active Operations menu item badge when loading/in-transit counts change. Must run in injection context. */
  private readonly updateActiveOperationsBadge = effect(() => {
    const count = this.totalActiveCount();
    const activeItem = this.operationsGroup.items.find((item) => item.label === 'Active Operations');
    if (activeItem) {
      activeItem.badge = count > 0 ? count : 0;
    }
  });

  parcelsGroup: SidebarNavGroup = {
    label: 'Parcels',
    icon: 'fe-package',
    defaultExpanded: true,
    items: [
      {
        label: 'Received',
        route: '/parcels/received',
        icon: 'fe-inbox',
        exact: true,
      },
      {
        label: 'Ready for Pickup',
        route: '/parcels/pickup',
        icon: 'fe-package',
        exact: true,
      },
      {
        label: 'Delivered',
        route: '/parcels/delivered',
        icon: 'fe-check-circle',
        exact: true,
      },
      {
        label: 'Delivery Manifests',
        route: '/parcels/deliveries',
        icon: 'fe-award',
        exact: true,
      },
      {
        label: 'Search',
        route: '/parcels/search',
        icon: 'fe-search',
        exact: true,
      },
      {
        label: 'Missing',
        route: '/parcels/missing',
        icon: 'fe-alert-circle',
        exact: true,
      },
      {
        label: 'Collections',
        route: '/parcels',
        icon: 'fa-cubes',
        iconType: 'fa',
        exact: true,
      },
      {
        label: 'Parcel Item Types',
        route: '/parcels/parcel-item-types',
        icon: 'fe-package',
        exact: true,
      },
    ],
  };

  /** Which parcel pages a clerk sees. Only decides what to show: the server refuses the work itself. */
  private parcelDutiesQuery = injectQuery(() => {
    const user = this.authUser() as { id?: string; role?: string } | null;
    return {
      queryKey: ['parcel-duties', user?.id],
      queryFn: () => lastValueFrom(this.tenantUsersApi.tenantUsersControllerGetParcelDuties({ id: user?.id ?? '' })),
      enabled: !!user?.id && user.role === TenantUserRoles.TERMINAL_PARCEL_CLERK,
    };
  });

  parcelsGroupForViewer = computed<SidebarNavGroup>(() => {
    const role = (this.authUser() as { role?: string } | null)?.role;
    const duties = this.parcelDutiesQuery.data();
    const shown = this.parcelsGroup.items.filter((item) => {
      // Bus stop staff take parcels over from a conductor and hand them to receivers.
      if (role === TenantUserRoles.BUS_STOP_MANAGER || role === TenantUserRoles.BUS_STOP_CLERK) {
        return BUS_STOP_PARCEL_PAGES.includes(item.label);
      }
      if (role !== TenantUserRoles.TERMINAL_PARCEL_CLERK || !duties) return true;
      if (COLLECTIONS_PAGES.includes(item.label)) return duties.collections;
      if (PICKUP_PAGES.includes(item.label)) return duties.pickup;
      return true;
    });
    return { ...this.parcelsGroup, items: shown };
  });

  manageGroup: SidebarNavGroup = {
    label: 'Manage',
    icon: 'fe-briefcase',
    defaultExpanded: true,
    items: [
      {
        label: 'My Terminal',
        route: '/routes/terminal',
        icon: 'fe-map-pin',
        roles: [
          TenantUserRoles.TENANT_TERMINAL_MANAGER,
          TenantUserRoles.TENANT_BOOKING_MANAGER,
          TenantUserRoles.TENANT_PARCEL_MANAGER,
        ],
      },
    ],
  };

  routesGroup: SidebarNavGroup = {
    label: 'Routes',
    icon: 'fe-map',
    defaultExpanded: true,
    items: [
      {
        label: 'Terminals',
        route: '/terminals',
        icon: 'fa-building-o',
        iconType: 'fa',
        roles: [TenantUserRoles.TENANT_ADMIN],
      },
      {
        label: 'Bus Stops',
        route: '/bus-stops',
        icon: 'fe-stop-circle',
        roles: [TenantUserRoles.TENANT_ADMIN],
      },
      {
        label: 'Routes',
        route: '/routes',
        icon: 'fe-map',
        roles: [TenantUserRoles.TENANT_ADMIN],
      },
    ],
  };

  fleetGroup: SidebarNavGroup = {
    label: 'Fleet',
    icon: 'fa-bus',
    iconType: 'fa',
    defaultExpanded: true,
    items: [
      {
        label: 'Buses',
        route: '/buses',
        icon: 'fa-bus',
        iconType: 'fa',
        roles: [TenantUserRoles.TENANT_ADMIN, TenantUserRoles.TENANT_TERMINAL_MANAGER],
      },
      {
        label: 'My Assigned Buses',
        route: '/buses/conductor/1',
        icon: 'fa-bus',
        iconType: 'fa',
        roles: [TenantUserRoles.TENANT_CONDUCTOR], // Only visible to Conductors
      },
      {
        label: 'Conductors',
        route: '/conductors',
        icon: 'fe-user-x',
        roles: [TenantUserRoles.TENANT_ADMIN, TenantUserRoles.TENANT_TERMINAL_MANAGER],
      },
      {
        label: 'Drivers',
        route: '/drivers',
        icon: 'fe-user-check',
        roles: [TenantUserRoles.TENANT_ADMIN, TenantUserRoles.TENANT_TERMINAL_MANAGER],
      },
    ],
  };

  accessControlGroup: SidebarNavGroup = {
    label: 'Access Control',
    icon: 'fe-shield',
    defaultExpanded: true,
    items: [
      {
        label: 'Users',
        route: '/access-control',
        icon: 'fe-shield',
        exact: true,
      },
    ],
  };

  /** Administration group for mobile drawer only (shown at bottom of scrollable list). */
  administrationGroup: SidebarNavGroup = {
    label: 'Administration',
    icon: 'fe-settings',
    defaultExpanded: true,
    items: [
      {
        label: 'Settings',
        route: '/settings/company',
        icon: 'fe-settings',
        exact: true,
      },
      {
        label: 'Billing',
        route: '/billing',
        icon: 'fe-credit-card',
        exact: true,
      },
    ],
  };

  ngOnInit() {
    // Emit initial collapsed state so parent layout knows the sidebar state on page load
    this.collapsedChange.emit(this.isCollapsed());

    // Filter menu groups based on user roles
    this.filterMenuGroups();
  }

  toggleCollapse() {
    const newState = !this.isCollapsed();
    this.isCollapsed.set(newState);
    this.saveCollapseState(newState);
    this.collapsedChange.emit(newState);
  }

  ontoggleMenu() {
    const state = !this.isOpen;
    this.toggleMenu.emit(state);
  }

  async canViewMenu(role: TenantUserRoles): Promise<boolean> {
    return await this.roleService.hasRole(role);
  }

  private filterMenuGroups() {
    // Menu items are filtered by ngxPermissionsOnly in template
    // This method can be used for additional filtering if needed
  }

  private loadCollapseState(): boolean {
    if (typeof window === 'undefined' || !window.localStorage) {
      return false;
    }

    try {
      const stored = localStorage.getItem('sidebar-collapsed');
      return stored === 'true';
    } catch {
      return false;
    }
  }

  private saveCollapseState(state: boolean): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    try {
      localStorage.setItem('sidebar-collapsed', String(state));
    } catch {
      // Ignore storage errors
    }
  }
}
